import React, { useState, useEffect, useCallback, useMemo } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { ColDef, ModuleRegistry } from "ag-grid-community";
import {
  CellStyleModule,
  PaginationModule,
  ClientSideRowModelModule,
} from "ag-grid-community";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  ResponsiveContainer,
} from "recharts";
import {
  format,
  parseISO,
  differenceInMonths,
  differenceInDays,
  isAfter,
  isBefore,
} from "date-fns";
import { toast } from "react-toastify";
import { Search, Filter, Calendar } from "lucide-react";
import {
  EquipmentHistory,
  getAllEquipmentManagementData,
  getAllEquipmentHistory,
} from "../../../service/api";
import {
  useEquipmentManagementStore,
  usePremisesStore,
  useRegionStore,
} from "../../../store/store";
import { Dropdown } from "../../../components/dropdown/Dropdown";
import { EquipmentInventory, MaintenanceTask } from "../../../types/api";

// Register ag-grid modules
ModuleRegistry.registerModules([
  CellStyleModule,
  PaginationModule,
  ClientSideRowModelModule,
]);

// Define colors for charts
const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
];

// Helper function to calculate KPIs generically
const calculateKPIs = (
  data: any[],
  calculations: Record<string, (data: any[]) => number | string>,
) => {
  return Object.fromEntries(
    Object.entries(calculations).map(([key, calc]) => [key, calc(data)]),
  );
};

const ReportAndAnalytics: React.FC = () => {
  const [equipmentData, setEquipmentData] = useState<EquipmentInventory[]>([]);
  const [historyData, setHistoryData] = useState<EquipmentHistory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedRegion, setSelectedRegion] = useState<string>("");
  const [selectedPremises, setSelectedPremises] = useState<string>("");
  const [dateRange, setDateRange] = useState<{ start: string; end: string }>({
    start: "",
    end: "",
  });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [dataLastFetched, setDataLastFetched] = useState<number>(0);
  const [expanded, setExpanded] = useState<string | false>(false);

  const { fetchAllMaintenanceSchedule, maintenanceTasks } =
    useEquipmentManagementStore();
  const { fetchPremises } = usePremisesStore();
  const { fetchRegions } = useRegionStore();

  // Memoize maintenanceTasks to prevent unnecessary re-renders
  const stableTasks = useMemo(() => maintenanceTasks || [], [maintenanceTasks]);

  // Fetch all necessary data - removed maintenanceTasks from dependencies
  const fetchData = useCallback(async () => {
    // Prevent fetching if we just fetched recently (debounce)
    const now = Date.now();
    if (now - dataLastFetched < 5000) {
      // 5 second debounce
      return;
    }

    setLoading(true);
    try {
      // Fetch equipment and history data
      const [equipmentRes, historyRes] = await Promise.all([
        getAllEquipmentManagementData(),
        getAllEquipmentHistory(),
      ]);

      setEquipmentData(
        (equipmentRes.data?.results as EquipmentInventory[]) || [],
      );
      setHistoryData(historyRes.data?.message || []);

      // Fetch maintenance tasks only if we don't have them or they're stale
      if (!maintenanceTasks || maintenanceTasks.length === 0) {
        await fetchAllMaintenanceSchedule();
      }

      // Fetch other reference data only if needed
      await Promise.all([fetchPremises(), fetchRegions()]);

      setDataLastFetched(now);
    } catch (error) {
      toast.error("Failed to fetch data for reports");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [
    fetchAllMaintenanceSchedule,
    fetchPremises,
    fetchRegions,
    maintenanceTasks,
    dataLastFetched,
  ]);

  // Initial data fetch - only run once on mount
  useEffect(() => {
    fetchData();
  }, []); // Empty dependency array - only run on mount

  // Manual refresh function
  const handleRefresh = useCallback(() => {
    setDataLastFetched(0); // Reset the debounce
    fetchData();
  }, [fetchData]);

  // Global filter function for all reports
  const applyGlobalFilters = useCallback(
    (data: any[]) => {
      return data.filter((item) => {
        const matchesSearch = Object.values(item).some((val) =>
          String(val).toLowerCase().includes(searchTerm.toLowerCase()),
        );
        const matchesRegion = selectedRegion
          ? item.region === selectedRegion
          : true;
        const matchesPremises = selectedPremises
          ? item.premises === selectedPremises
          : true;
        const matchesDate =
          dateRange.start && dateRange.end
            ? isAfter(
                parseISO(item.date || item.eventDate || item.scheduledDate),
                parseISO(dateRange.start),
              ) &&
              isBefore(
                parseISO(item.date || item.eventDate || item.scheduledDate),
                parseISO(dateRange.end),
              )
            : true;
        return matchesSearch && matchesRegion && matchesPremises && matchesDate;
      });
    },
    [searchTerm, selectedRegion, selectedPremises, dateRange],
  );

  // Handle search input change
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) =>
    setSearchTerm(e.target.value);

  // Handle date range change
  const handleDateChange = (field: "start" | "end", value: string) => {
    setDateRange((prev) => ({ ...prev, [field]: value }));
  };

  // Handle accordion expansion
  const handleChange =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpanded(isExpanded ? panel : false);
    };

  // Memoize filtered data to prevent unnecessary recalculations
  const filteredEquipmentData = useMemo(
    () => applyGlobalFilters(equipmentData),
    [equipmentData, applyGlobalFilters],
  );

  const filteredHistoryData = useMemo(
    () => applyGlobalFilters(historyData),
    [historyData, applyGlobalFilters],
  );

  const filteredTaskData = useMemo(
    () => applyGlobalFilters(stableTasks),
    [stableTasks, applyGlobalFilters],
  );

  if (loading) {
    return (
      <div className="mt-14 text-center py-8">Loading reports data...</div>
    );
  }

  return (
    <div className="px-2 py-4 mt-14  min-h-screen">
      <h1 className="text-2xl font-semibold mb-6">Reports and Analytics</h1>

      {/* Global Filters */}
      <div className="flex flex-wrap gap-4 mb-6 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search
            className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500"
            size={18}
          />
          <input
            type="text"
            placeholder="Search across all reports..."
            value={searchTerm}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md"
          />
        </div>
        <Dropdown
          selectedValue={selectedRegion}
          onChange={setSelectedRegion}
          type="region"
        />
        <Dropdown
          selectedValue={selectedPremises}
          onChange={setSelectedPremises}
          type="premises"
          selectedRegion={selectedRegion}
        />
        <div className="flex items-center gap-2">
          <Calendar size={18} className="text-gray-500" />
          <input
            type="date"
            value={dateRange.start}
            onChange={(e) => handleDateChange("start", e.target.value)}
            className="p-2 border border-gray-300 rounded-md"
          />
          <span>to</span>
          <input
            type="date"
            value={dateRange.end}
            onChange={(e) => handleDateChange("end", e.target.value)}
            className="p-2 border border-gray-300 rounded-md"
          />
        </div>
        <button
          onClick={handleRefresh}
          disabled={loading}
          className="px-4 py-2 bg-[#0fa9a6] text-white rounded-md flex items-center gap-2 disabled:opacity-50"
        >
          <Filter size={18} /> Refresh
        </button>
      </div>

      {/* Equipment Inventory Report */}
      <Accordion
        expanded={expanded === "panel1"}
        onChange={handleChange("panel1")}
        sx={{ marginBottom: "10px" }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className="text-black " />}
          aria-controls="panel1bh-content"
          id="panel1bh-header"
          sx={{
            background: "white",
            color: "black",
          }}
        >
          <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
            Equipment Inventory Report
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <div className="mb-6">
              <p className="text-gray-600 mb-6">
                Comprehensive overview of all equipment assets, including status
                and location.
              </p>

              {/* Side-by-side layout: Chart on left, KPIs on right */}
              <div className="flex gap-6 mb-6">
                {/* Chart Section - Takes more space (70%) */}
                <div className="flex-[7] bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium mb-4 text-center">
                    Equipment Status Distribution
                  </h3>
                  {(() => {
                    const statusData = [
                      {
                        name: "Active",
                        value: filteredEquipmentData.filter(
                          (e) => e.status === "Active",
                        ).length,
                      },
                      {
                        name: "In Repair",
                        value: filteredEquipmentData.filter(
                          (e) => e.status === "In Repair",
                        ).length,
                      },
                      {
                        name: "Decommissioned",
                        value: filteredEquipmentData.filter(
                          (e) => e.status === "Decommissioned",
                        ).length,
                      },
                    ];
                    return (
                      <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                          <Pie
                            data={statusData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={120}
                          >
                            {statusData.map((_, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </div>

                {/* KPIs Section - Takes less space (30%) */}
                <div className="flex-[3]">
                  {(() => {
                    const kpis = calculateKPIs(filteredEquipmentData, {
                      totalAssets: (d) => d.length,
                      activePercentage: (d) =>
                        d.length > 0
                          ? (
                              (d.filter((e) => e.status === "Active").length /
                                d.length) *
                              100
                            ).toFixed(2)
                          : "0",
                      avgAge: (d) =>
                        d.length > 0
                          ? (
                              d.reduce(
                                (sum, e) =>
                                  sum +
                                  differenceInDays(
                                    new Date(),
                                    parseISO(e.purchaseDate),
                                  ),
                                0,
                              ) /
                              d.length /
                              365
                            ).toFixed(1)
                          : "0",
                    });
                    return (
                      <div className="space-y-4">
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                          <h3 className="font-medium text-gray-700">
                            Total Assets
                          </h3>
                          <p className="text-3xl font-bold text-blue-600">
                            {kpis.totalAssets}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                          <h3 className="font-medium text-gray-700">
                            Active %
                          </h3>
                          <p className="text-3xl font-bold text-green-600">
                            {kpis.activePercentage}%
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-orange-500">
                          <h3 className="font-medium text-gray-700">
                            Avg Age (Years)
                          </h3>
                          <p className="text-3xl font-bold text-orange-600">
                            {kpis.avgAge}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Table Section */}
              {/* <div className="ag-theme-alpine mt-6" style={{ height: 400 }}>
          <AgGridReact
            rowData={filteredEquipmentData}
            columnDefs={[
              { headerName: "ID", field: "equipmentId", flex: 1 },
              { headerName: "Name", field: "equipmentName", flex: 1.5 },
              { headerName: "Category", field: "category", flex: 1 },
              { headerName: "Status", field: "status", flex: 1 },
              { 
                headerName: "Location", 
                flex: 1.5,
                valueGetter: (params) => `${params.data.region || 'N/A'} / ${params.data.premises || 'N/A'}` 
              },
              { 
                headerName: "Purchase Date", 
                field: "purchaseDate", 
                flex: 1.2,
                valueFormatter: (params) => {
                  try {
                    return params.value ? format(parseISO(params.value), "MMM dd, yyyy") : 'N/A';
                  } catch {
                    return 'Invalid Date';
                  }
                }
              },
            ] as ColDef[]}
            pagination={true}
            paginationPageSize={10}
            domLayout="normal"
            defaultColDef={{
              sortable: true,
              resizable: true,
              filter: true,
            }}
          />
        </div> */}
            </div>
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Warranty and Remediation Report */}
      <Accordion
        expanded={expanded === "panel2"}
        onChange={handleChange("panel2")}
        sx={{ marginBottom: "10px" }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className="text-black" />}
          aria-controls="panel2bh-content"
          id="panel2bh-header"
          sx={{ background: "white", color: "black" }}
        >
          <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
            Warranty and Remediation Report
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <div className="mb-6">
              <p className="text-gray-600 mb-6">
                Tracking warranties and associated remediation actions for
                timely interventions.
              </p>

              {/* Side-by-side layout */}
              <div className="flex gap-6 mb-6">
                <div className="flex-[7] bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium mb-4 text-center">
                    Warranty Expiry Timeline
                  </h3>
                  {(() => {
                    const expiryData = filteredEquipmentData
                      .map((e) => ({
                        name:
                          e.equipmentName?.substring(0, 15) + "..." ||
                          "Unknown",
                        monthsToExpiry: differenceInMonths(
                          parseISO(e.warrantyExpiryDate),
                          new Date(),
                        ),
                      }))
                      .filter((item) => item.monthsToExpiry <= 12) // Show only items expiring within 12 months
                      .sort((a, b) => a.monthsToExpiry - b.monthsToExpiry)
                      .slice(0, 10);
                    return (
                      <ResponsiveContainer width="100%" height={350}>
                        <BarChart data={expiryData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="name"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="monthsToExpiry" fill="#FF8042" />
                        </BarChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </div>

                <div className="flex-[3]">
                  {(() => {
                    const warrantyData = filteredEquipmentData.filter(
                      (e) =>
                        differenceInMonths(
                          parseISO(e.warrantyExpiryDate),
                          new Date(),
                        ) <= 3,
                    );
                    const kpis = calculateKPIs(warrantyData, {
                      expiringSoon: (d) => d.length,
                      remediationCompletion: (d) =>
                        d.length > 0
                          ? (
                              (d.filter((e) => e.remediationAction?.action)
                                .length /
                                d.length) *
                              100
                            ).toFixed(2)
                          : "0",
                      avgDaysToExpiry: (d) =>
                        d.length > 0
                          ? (
                              d.reduce(
                                (sum, e) =>
                                  sum +
                                  differenceInDays(
                                    parseISO(e.warrantyExpiryDate),
                                    new Date(),
                                  ),
                                0,
                              ) / d.length
                            ).toFixed(1)
                          : "0",
                    });
                    return (
                      <div className="space-y-4">
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
                          <h3 className="font-medium text-gray-700">
                            Expiring Soon
                          </h3>
                          <p className="text-3xl font-bold text-red-600">
                            {kpis.expiringSoon}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                          <h3 className="font-medium text-gray-700">
                            Remediation %
                          </h3>
                          <p className="text-3xl font-bold text-blue-600">
                            {kpis.remediationCompletion}%
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-yellow-500">
                          <h3 className="font-medium text-gray-700">
                            Avg Days to Expiry
                          </h3>
                          <p className="text-3xl font-bold text-yellow-600">
                            {kpis.avgDaysToExpiry}
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* <div className="ag-theme-alpine mt-6" style={{ height: 400 }}>
          <AgGridReact
            rowData={filteredEquipmentData.filter(
              (e) =>
                differenceInMonths(
                  parseISO(e.warrantyExpiryDate),
                  new Date()
                ) <= 12
            )}
            columnDefs={
              [
                { headerName: "ID", field: "equipmentId", flex: 1 },
                { headerName: "Name", field: "equipmentName", flex: 1.5 },
                {
                  headerName: "Expiry Date",
                  field: "warrantyExpiryDate",
                  flex: 1.2,
                  valueFormatter: (params) => {
                    try {
                      return params.value
                        ? format(parseISO(params.value), "MMM dd, yyyy")
                        : "N/A";
                    } catch {
                      return "Invalid Date";
                    }
                  },
                },
                {
                  headerName: "Months to Expiry",
                  flex: 1,
                  valueGetter: (params) =>
                    differenceInMonths(
                      parseISO(params.data.warrantyExpiryDate),
                      new Date()
                    ),
                },
                {
                  headerName: "Action",
                  field: "remediationAction.action",
                  flex: 1.5,
                },
                {
                  headerName: "Responsible",
                  field: "remediationAction.responsiblePerson",
                  flex: 1.5,
                },
              ] as ColDef[]
            }
            pagination={true}
            paginationPageSize={10}
            domLayout="normal"
            defaultColDef={{
              sortable: true,
              resizable: true,
              filter: true,
            }}
          />
        </div> */}
            </div>
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Maintenance Task Report */}
      <Accordion
        expanded={expanded === "panel3"}
        onChange={handleChange("panel3")}
        sx={{ marginBottom: "10px" }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className="text-black" />}
          aria-controls="panel3bh-content"
          id="panel3bh-header"
          sx={{ background: "white", color: "black" }}
        >
          <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
            Maintenance Task Report
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <div className="mb-6">
              <p className="text-gray-600 mb-6">
                Overview of maintenance tasks, statuses, and performance
                metrics.
              </p>

              <div className="flex gap-6 mb-6">
                <div className="flex-[7] bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium mb-4 text-center">
                    Task Status Distribution
                  </h3>
                  {(() => {
                    const statusData = [
                      {
                        name: "Scheduled",
                        value: filteredTaskData.filter(
                          (t) => t.status === "Scheduled",
                        ).length,
                      },
                      {
                        name: "In Progress",
                        value: filteredTaskData.filter(
                          (t) => t.status === "In Progress",
                        ).length,
                      },
                      {
                        name: "Completed",
                        value: filteredTaskData.filter(
                          (t) => t.status === "Completed",
                        ).length,
                      },
                      {
                        name: "Overdue",
                        value: filteredTaskData.filter(
                          (t) => t.status === "Overdue",
                        ).length,
                      },
                    ];
                    return (
                      <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                          <Pie
                            data={statusData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={120}
                          >
                            {statusData.map((_, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </div>

                <div className="flex-[3]">
                  {(() => {
                    const kpis = calculateKPIs(filteredTaskData, {
                      totalTasks: (d) => d.length,
                      completionRate: (d) =>
                        d.length > 0
                          ? (
                              (d.filter((t) => t.status === "Completed")
                                .length /
                                d.length) *
                              100
                            ).toFixed(2)
                          : "0",
                      overduePercentage: (d) =>
                        d.length > 0
                          ? (
                              (d.filter((t) => t.status === "Overdue").length /
                                d.length) *
                              100
                            ).toFixed(2)
                          : "0",
                    });
                    return (
                      <div className="space-y-4">
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                          <h3 className="font-medium text-gray-700">
                            Total Tasks
                          </h3>
                          <p className="text-3xl font-bold text-blue-600">
                            {kpis.totalTasks}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                          <h3 className="font-medium text-gray-700">
                            Completion %
                          </h3>
                          <p className="text-3xl font-bold text-green-600">
                            {kpis.completionRate}%
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
                          <h3 className="font-medium text-gray-700">
                            Overdue %
                          </h3>
                          <p className="text-3xl font-bold text-red-600">
                            {kpis.overduePercentage}%
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* <div className="ag-theme-alpine mt-6" style={{ height: 400 }}>
          <AgGridReact
            rowData={filteredTaskData}
            columnDefs={
              [
                { headerName: "Task ID", field: "taskId", flex: 1 },
                { headerName: "Equipment", field: "equipmentName", flex: 1.5 },
                { headerName: "Type", field: "maintenanceType", flex: 1 },
                { headerName: "Status", field: "status", flex: 1 },
                {
                  headerName: "Scheduled",
                  field: "scheduledDate",
                  flex: 1.2,
                  valueFormatter: (params) => {
                    try {
                      return params.value
                        ? format(parseISO(params.value), "MMM dd, yyyy")
                        : "N/A";
                    } catch {
                      return "Invalid Date";
                    }
                  },
                },
                { headerName: "Assigned To", field: "assignedTo", flex: 1.5 },
              ] as ColDef[]
            }
            pagination={true}
            paginationPageSize={10}
            domLayout="normal"
            defaultColDef={{
              sortable: true,
              resizable: true,
              filter: true,
            }}
          />
        </div> */}
            </div>
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Equipment History and Events Report */}
      <Accordion
        expanded={expanded === "panel4"}
        onChange={handleChange("panel4")}
        sx={{ marginBottom: "10px" }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className="text-black" />}
          aria-controls="panel4bh-content"
          id="panel4bh-header"
          sx={{ background: "white", color: "black" }}
        >
          <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
            Equipment History and Events Report
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <div className="mb-6">
              <p className="text-gray-600 mb-6">
                Chronological log of equipment events for lifecycle analysis.
              </p>

              <div className="flex gap-6 mb-6">
                <div className="flex-[7] bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium mb-4 text-center">
                    Event Trends Over Time
                  </h3>
                  {(() => {
                    const trendData = filteredHistoryData.reduce(
                      (acc, h) => {
                        const month = format(parseISO(h.eventDate), "MMM yyyy");
                        acc[month] = (acc[month] || 0) + 1;
                        return acc;
                      },
                      {} as Record<string, number>,
                    );
                    const lineData = Object.entries(trendData)
                      .sort(
                        ([a], [b]) =>
                          new Date(a).getTime() - new Date(b).getTime(),
                      )
                      .map(([name, value]) => ({ name, value }));
                    return (
                      <ResponsiveContainer width="100%" height={350}>
                        <LineChart data={lineData}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            dataKey="name"
                            angle={-45}
                            textAnchor="end"
                            height={80}
                          />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="value"
                            stroke="#8884d8"
                            strokeWidth={3}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </div>

                <div className="flex-[3]">
                  {(() => {
                    const kpis = calculateKPIs(filteredHistoryData, {
                      totalEvents: (d) => d.length,
                      avgResolutionDays: (d) =>
                        d.length > 0
                          ? (
                              d.reduce(
                                (sum, h) =>
                                  sum +
                                  (h.completionDate
                                    ? differenceInDays(
                                        parseISO(h.completionDate),
                                        parseISO(h.eventDate),
                                      )
                                    : 0),
                                0,
                              ) / d.filter((h) => h.completionDate).length || 0
                            ).toFixed(1)
                          : "0",
                      incidentRate: (d) =>
                        d.length > 0
                          ? (
                              (d.filter((h) => h.eventType === "Incident")
                                .length /
                                d.length) *
                              100
                            ).toFixed(2)
                          : "0",
                    });
                    return (
                      <div className="space-y-4">
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                          <h3 className="font-medium text-gray-700">
                            Total Events
                          </h3>
                          <p className="text-3xl font-bold text-blue-600">
                            {kpis.totalEvents}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                          <h3 className="font-medium text-gray-700">
                            Avg Resolution (Days)
                          </h3>
                          <p className="text-3xl font-bold text-green-600">
                            {kpis.avgResolutionDays}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-red-500">
                          <h3 className="font-medium text-gray-700">
                            Incident %
                          </h3>
                          <p className="text-3xl font-bold text-red-600">
                            {kpis.incidentRate}%
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* <div className="ag-theme-alpine mt-6" style={{ height: 400 }}>
          <AgGridReact
            rowData={filteredHistoryData}
            columnDefs={
              [
                { headerName: "Event ID", field: "eventId", flex: 1 },
                { headerName: "Equipment", field: "equipmentName", flex: 1.5 },
                { headerName: "Type", field: "eventType", flex: 1 },
                {
                  headerName: "Date",
                  field: "eventDate",
                  flex: 1.2,
                  valueFormatter: (params) => {
                    try {
                      return params.value
                        ? format(parseISO(params.value), "MMM dd, yyyy")
                        : "N/A";
                    } catch {
                      return "Invalid Date";
                    }
                  },
                },
                {
                  headerName: "Cost",
                  field: "cost",
                  flex: 1,
                  valueFormatter: (params) =>
                    params.value ? `${params.value.toLocaleString()}` : "$0",
                },
                { headerName: "Performed By", field: "performedBy", flex: 1.5 },
              ] as ColDef[]
            }
            pagination={true}
            paginationPageSize={10}
            domLayout="normal"
            defaultColDef={{
              sortable: true,
              resizable: true,
              filter: true,
            }}
          />
        </div> */}
            </div>
          </Typography>
        </AccordionDetails>
      </Accordion>

      {/* Cost Analysis Report */}
      <Accordion
        expanded={expanded === "panel5"}
        onChange={handleChange("panel5")}
        sx={{ marginBottom: "10px" }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon className="text-black" />}
          aria-controls="panel5bh-content"
          id="panel5bh-header"
          sx={{ background: "white", color: "black" }}
        >
          <Typography component="span" sx={{ width: "33%", flexShrink: 0 }}>
            Cost Analysis Report
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Typography>
            <div className="mb-6">
              <p className="text-gray-600 mb-6">
                Breakdown of costs associated with equipment maintenance and
                ownership.
              </p>

              <div className="flex gap-6 mb-6">
                <div className="flex-[7] bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium mb-4 text-center">
                    Cost Breakdown by Type
                  </h3>
                  {(() => {
                    const costData = [
                      {
                        name: "Purchase",
                        value: filteredHistoryData
                          .filter((h) => h.eventType === "Purchase")
                          .reduce((sum, h) => sum + (h.cost || 0), 0),
                      },
                      {
                        name: "Maintenance",
                        value: filteredHistoryData
                          .filter((h) => h.eventType === "Maintenance")
                          .reduce((sum, h) => sum + (h.cost || 0), 0),
                      },
                      {
                        name: "Repair",
                        value: filteredHistoryData
                          .filter((h) => h.eventType === "Repair")
                          .reduce((sum, h) => sum + (h.cost || 0), 0),
                      },
                      {
                        name: "Other",
                        value: filteredHistoryData
                          .filter(
                            (h) =>
                              !["Purchase", "Maintenance", "Repair"].includes(
                                h.eventType,
                              ),
                          )
                          .reduce((sum, h) => sum + (h.cost || 0), 0),
                      },
                    ].filter((item) => item.value > 0); // Only show categories with costs
                    return (
                      <ResponsiveContainer width="100%" height={350}>
                        <PieChart>
                          <Pie
                            data={costData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={120}
                          >
                            {costData.map((_, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip
                            formatter={(value) =>
                              `${Number(value).toLocaleString()}`
                            }
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </div>

                <div className="flex-[3]">
                  {(() => {
                    const totalCost = filteredHistoryData.reduce(
                      (sum, h) => sum + (h.cost || 0),
                      0,
                    );
                    const kpis = calculateKPIs(filteredHistoryData, {
                      totalCost: () => totalCost.toFixed(2),
                      avgCostPerEvent: (d) =>
                        d.length > 0 ? (totalCost / d.length).toFixed(2) : "0",
                      maintenanceCostPercentage: (d) =>
                        totalCost > 0
                          ? (
                              (d
                                .filter(
                                  (h) =>
                                    h.eventType === "Maintenance" ||
                                    h.eventType === "Repair",
                                )
                                .reduce((sum, h) => sum + (h.cost || 0), 0) /
                                totalCost) *
                              100
                            ).toFixed(2)
                          : "0",
                    });
                    return (
                      <div className="space-y-4">
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
                          <h3 className="font-medium text-gray-700">
                            Total Cost
                          </h3>
                          <p className="text-3xl font-bold text-green-600">
                            ${kpis.totalCost}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
                          <h3 className="font-medium text-gray-700">
                            Avg Cost/Event
                          </h3>
                          <p className="text-3xl font-bold text-blue-600">
                            ${kpis.avgCostPerEvent}
                          </p>
                        </div>
                        <div className="bg-white p-4 rounded-lg shadow border-l-4 border-orange-500">
                          <h3 className="font-medium text-gray-700">
                            Maintenance %
                          </h3>
                          <p className="text-3xl font-bold text-orange-600">
                            {kpis.maintenanceCostPercentage}%
                          </p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* <div className="ag-theme-alpine mt-6" style={{ height: 400 }}>
          <AgGridReact
            rowData={filteredHistoryData.filter((h) => h.cost > 0)}
            columnDefs={
              [
                { headerName: "Event ID", field: "eventId", flex: 1 },
                { headerName: "Equipment", field: "equipmentName", flex: 1.5 },
                { headerName: "Type", field: "eventType", flex: 1 },
                {
                  headerName: "Date",
                  field: "eventDate",
                  flex: 1.2,
                  valueFormatter: (params) => {
                    try {
                      return params.value
                        ? format(parseISO(params.value), "MMM dd, yyyy")
                        : "N/A";
                    } catch {
                      return "Invalid Date";
                    }
                  },
                },
                {
                  headerName: "Cost",
                  field: "cost",
                  flex: 1,
                  valueFormatter: (params) =>
                    params.value ? `${params.value.toLocaleString()}` : "$0",
                },
              ] as ColDef[]
            }
            pagination={true}
            paginationPageSize={10}
            domLayout="normal"
            defaultColDef={{
              sortable: true,
              resizable: true,
              filter: true,
            }}
          />
        </div> */}
            </div>
          </Typography>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

export default ReportAndAnalytics;
